/* Primaccino Binder Studio V1.5 — quote manifest v1, secure handoff disclosure; no app credentials, payments or image upload backend. */
(() => {
  const GALLERY_KEY='primaccino:selected-artwork:v1';
  const REFERENCE_KEY='primaccino:inspiration-ref:v1';
  const DRAFT_KEY='primaccino:studio-wizard-draft:v0971'; // preserve customer projects from V0.9.7.1
  const DRAFT_TTL=7*24*60*60*1000;
  const REQUEST_KEY='primaccino:quote-request-id:v1';
  const LAST_SUBMITTED_KEY='primaccino:last-submitted-quote:v1';
  const LAST_UPLOAD_KEY='primaccino:last-quote-had-image:v096';
  const COLOR=/^#[0-9a-f]{6}$/i;
  const PROCESS='Impression puis peinture à la main';
  function boot(scope=document){
    const roots=[];
    if(scope.matches?.('[data-binder-studio]')) roots.push(scope);
    scope.querySelectorAll?.('[data-binder-studio]').forEach(node=>roots.push(node));
    roots.forEach(root=>{
      try{init(root)}catch(error){
        root.dataset.basInitialized='error';
        const message=root.querySelector('[data-bas="status-text"]');
        if(message)message.textContent='Le studio rencontre une erreur de chargement. Actualisez la page ou contactez l’atelier.';
        console.error('[Primaccino Studio] Erreur d’initialisation :',error);
      }
    });
  }
  function init(root){
    if(root.dataset.basInitialized==='yes')return;
    root.dataset.basInitialized='yes';
    const by=name=>root.querySelector(`[data-bas="${name}"]`);
    const art=by('artwork'), artOnly=by('art-only');
    const sampleArt=root.dataset.exampleArt;
    const settings=name=>Number(root.dataset[name])||0;
    const state={gallery:null,inspiration:null,source:'example',view:'front',image:sampleArt,uploaded:null,uploadName:'',uploadMeta:null,referenceToken:0};
    const read=name=>(by(name)?.value||'').trim();
    // Prevent multiline user-supplied labels from mimicking merchant fields in the readable request.
    const line=(value,max=180)=>String(value??'').replace(/[\u0000-\u001f\u007f]+/g,' ').replace(/\s+/g,' ').trim().slice(0,max);
    const colourChoice=()=>root.querySelector('[name^="bas-binder-color-"]:checked');
    const binderColor=()=>colourChoice()?.value||'Blanc';
    const binderHex=()=>COLOR.test(colourChoice()?.dataset.colorHex||'')?colourChoice().dataset.colorHex:'#F6F5F3';
    const val=(name,fallback=50)=>Number(by(name)?.value??fallback);
    const money=amount=>new Intl.NumberFormat('fr-CA',{style:'currency',currency:'CAD',maximumFractionDigits:2}).format(amount);
    const COMPLEXITIES={simple:['Simple','simpleHours',1],moderate:['Modérée','moderateHours',2],detailed:['Détaillée','detailedHours',4],complex:['Très détaillée','complexHours',6]};
    const selectedComplexity=()=>COMPLEXITIES[read('complexity')]?read('complexity'):'moderate';
    const complexityName=()=>state.gallery?'Évaluation de l’artiste':COMPLEXITIES[selectedComplexity()][0];
    const productionHours=()=>state.gallery?.estimatedHours ?? (settings(COMPLEXITIES[selectedComplexity()][1]) || COMPLEXITIES[selectedComplexity()][2]);
    const precision=hours=>new Intl.NumberFormat('fr-CA',{maximumFractionDigits:1}).format(hours);
    // One calculation feeds the displayed quote and the Shopify contact request.
    // These are customer-facing PRICE components, not merchant cost basis or net profit.
    function pricing(){
      const selectedSize=by('binder-size')?.selectedOptions?.[0];
      const sizeFee=Number(selectedSize?.dataset.surcharge)||0;
      const hours=productionHours();
      const price={base:settings('basePrice'),sizeFee,hours,hourly:settings('hourlyRate'),reseller:settings('resellerFee'),printing:settings('printingCost'),materials:settings('materialCost')};
      price.labor=price.hours*price.hourly;
      price.total=price.base+price.sizeFee+price.labor+price.reseller+price.printing+price.materials;
      return price;
    }
    const total=()=>pricing().total;
    function status(message){by('status-text').textContent=message;}
    function newRequestId(){
      // Human-readable correlation reference, not an order number or proof of payment.
      const n=new Uint8Array(5);
      if(globalThis.crypto?.getRandomValues)crypto.getRandomValues(n);
      else {for(let i=0;i<n.length;i++)n[i]=Math.floor(Math.random()*256)}
      const hex=Array.from(n,b=>b.toString(16).padStart(2,'0')).join('').toUpperCase();
      return 'PR-'+new Date().toISOString().slice(2,10).replace(/-/g,'')+'-'+hex;
    }
    let requestId='';
    try{requestId=sessionStorage.getItem(REQUEST_KEY)||''}catch{}
    if(!/^PR-\d{6}-[0-9A-F]{10}$/.test(requestId))requestId=newRequestId();
    try{sessionStorage.setItem(REQUEST_KEY,requestId)}catch{}
    if(by('request-id'))by('request-id').textContent=requestId;
    const artworkEmail=(root.dataset.artworkContactEmail||'').trim();
    const validArtworkEmail=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(artworkEmail) && !/[?&#\r\n]/.test(artworkEmail);
    function imageMailHref(reference){
      const subject=`Primaccino - fichier du projet ${reference}`;
      const body=`Bonjour,\n\nVoici le fichier image pour mon projet ${reference}.\n\nJe vais joindre le fichier original à ce courriel.\n\nMerci !`;
      return `mailto:${artworkEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    function renderUploadHandoff(){
      const attached=!!state.uploaded;
      by('upload-handoff').hidden=!attached;
      by('upload-quote-note').hidden=!attached;
      by('upload-filename').textContent=state.uploadName||'—';
      const confirm=by('upload-confirm');
      confirm.required=attached;
      if(!attached)confirm.checked=false;
      const link=by('upload-mail');
      link.hidden=!attached||!validArtworkEmail;
      if(attached&&validArtworkEmail)link.href=imageMailHref(requestId);
      by('upload-no-mail').hidden=!attached||validArtworkEmail;
      refreshReadiness();
    }
    function refreshReadiness(){
      const design=by('readiness-design');
      const file=by('readiness-file');
      if(design)design.textContent=state.gallery?`Création de l’atelier : ${line(state.gallery.title,75)}`:
        state.inspiration?`${line(state.inspiration.name,75)} (inspiration à valider)`:
        state.uploaded?`Visuel client : ${line(state.uploadName,75)}`:
        read('subject')||read('details')?'Projet sur mesure décrit, à confirmer':'Choisis une illustration ou décris ton idée.';
      if(file)file.textContent=state.uploaded?
        validArtworkEmail?'Transmets aussi ton fichier original à l’atelier par courriel avec la référence du devis.':'L’atelier devra récupérer ton fichier original après la demande.':
        'Aucun fichier client à transmettre séparément.';
    }

    function specs(){return {subject:read('subject').slice(0,100),color:binderColor(),size:read('binder-size').slice(0,100),details:read('details').slice(0,500),zoom:val('zoom',100),horizontal:val('horizontal'),vertical:val('vertical')};}
    function update(){
      const d=specs();
      root.style.setProperty('--bas-position-x',`${d.horizontal}%`);
      root.style.setProperty('--bas-position-y',`${d.vertical}%`);
      by('binder').style.setProperty('--bas-binder-color',binderHex());
      if(state.source==='reference')showCardCrop();
      renderArtwork(); // Same cover-fill mathematics for approved and reference artwork.
      by('binder-color-tag').textContent=`CLASSEUR ${d.color.toLocaleUpperCase('fr')}`;
      by('zoom-value').textContent=d.zoom+' %';
      by('horizontal-value').textContent=d.horizontal===50?'Centre':d.horizontal+' %';
      by('vertical-value').textContent=d.vertical===50?'Centre':d.vertical+' %';
      by('preview-name').textContent=state.gallery?state.gallery.title:state.inspiration?`${state.inspiration.name} · Référence à étudier`:d.subject?`${d.subject} · Projet sur mesure`:'Votre classeur personnalisé';
      by('format-summary').textContent=`✓ ${d.color} · ${d.size} · disponible`;
      by('preview-specs').textContent=`${d.size} · ${d.color} · Impression + peinture`;
      by('estimate-hours').textContent=`Complexité : ${complexityName()} · ${precision(productionHours())} h de peinture estimées · à confirmer par l’artiste`;
      by('complexity').disabled=!!state.gallery;
      by('complexity-note').textContent=state.gallery?`Temps de peinture renseigné par l’atelier pour « ${state.gallery.title} » : ${precision(productionHours())} h. L’artiste confirme le devis.`:state.inspiration?'Suggestion selon le type de carte, pas une analyse automatique de l’image. Ajustez en fonction des détails à peindre ; l’artiste valide ensuite.':'Sélectionnez le niveau de détails du dessin à réaliser. Estimation indicative, ajustable par l’artiste.';
      const price=pricing();
      by('price').textContent=money(price.total);
      by('price-binder').textContent=money(price.base);
      by('price-size').textContent=money(price.sizeFee);
      by('price-size-row').hidden=price.sizeFee<=0;
      by('price-hours').textContent=`(${precision(price.hours)} h × ${money(price.hourly)})`;
      by('price-labor').textContent=money(price.labor);
      by('price-reseller').textContent=money(price.reseller);
      by('price-printing').textContent=money(price.printing);
      by('price-printing-row').hidden=price.printing<=0;
      by('price-material').textContent=money(price.materials);
      by('price-material-row').hidden=price.materials<=0;
      // The customer estimate is revenue if accepted, NOT net business profit.
      refreshReadiness();
    }
    function showReference(show){
      by('reference-cover').hidden=!show;by('reference-art').hidden=!show;
      art.hidden=show;artOnly.hidden=show;
      by('crop-settings').hidden=!show;
      by('reference-preview-note').hidden=!show;
      const zoomInput=by('zoom');
      zoomInput.min=show?'90':'100';zoomInput.max=show?'145':'180';
      if(show&&Number(zoomInput.value)>145)zoomInput.value='100';
      if(!show&&Number(zoomInput.value)<100)zoomInput.value='100';
      updateDownloadAvailability();
    }
    function switchArt(url,source){
      state.referenceToken++;showReference(false);
      state.image=url;state.source=source;
      art.src=url;artOnly.src=url;
      // Wait for native image dimensions before computing a non-distorted cover fit.
      requestAnimationFrame(renderArtwork);
      const alternative=source==='gallery'?'Illustration autorisée sélectionnée pour le classeur':source==='upload'?'Visuel fourni par le client':'Illustration fictive utilisée uniquement dans la maquette';
      art.alt=alternative;artOnly.alt=alternative;
      update();
    }
    function allowedImage(raw){try {const u=new URL(raw,location.href);return (u.protocol==='https:' || (u.protocol==='http:'&&u.origin===location.origin)) && (u.origin===location.origin||u.hostname==='cdn.shopify.com'||u.hostname.endsWith('.shopifycdn.net'));}catch{return false}}
    function removeInspiration(clear=true){
      state.referenceToken++;
      // When a Pokémon reference fills the subject field automatically, don't leave
      // that obsolete Pokémon in the quote after switching to approved artwork.
      // Preserve a subject the customer deliberately changed.
      const previousName=state.inspiration?.name;
      if(previousName && read('subject')===previousName)by('subject').value='';
      state.inspiration=null;by('inspiration').hidden=true;
      if(clear)try{sessionStorage.removeItem(REFERENCE_KEY)}catch{}
      if(state.source==='reference')switchArt(sampleArt,'example');
      update();
    }
    function validCardImage(base){try{const u=new URL(base);return u.origin==='https://assets.tcgdex.net'&&(/^\/(en|fr)\//).test(u.pathname)&&/^[a-zA-Z0-9_./-]+$/.test(u.pathname)&&!u.pathname.includes('..')&&!u.search&&!u.hash?u.origin+u.pathname.replace(/\/$/,''):''}catch{return ''}}
    // Shared crop/cover solver used by both the DOM preview and the PNG renderer.
    // The crop is a fractional rectangle of the original image; the image is never stretched.
    function coverGeometry(imageWidth,imageHeight,viewportWidth,viewportHeight,crop,zoom,horizontal,vertical,fitMode='cover'){
      if(!(imageWidth>0&&imageHeight>0&&viewportWidth>0&&viewportHeight>0))return null;
      const nativeScale=fitMode==='contain'?Math.min(viewportWidth/(imageWidth*crop.w),viewportHeight/(imageHeight*crop.h)):Math.max(viewportWidth/(imageWidth*crop.w),viewportHeight/(imageHeight*crop.h));
      const scale=nativeScale*(zoom/100);
      const width=imageWidth*scale,height=imageHeight*scale;
      const excessX=crop.w*width-viewportWidth,excessY=crop.h*height-viewportHeight;
      return {width,height,left:-crop.x*width-excessX*horizontal/100,top:-crop.y*height-excessY*vertical/100};
    }
    function applyCoverLayout(viewport,img,crop,fitMode='cover'){
      if(!viewport||!img||!img.naturalWidth||!img.naturalHeight)return;
      const d=specs();
      const geometry=coverGeometry(img.naturalWidth,img.naturalHeight,viewport.clientWidth,viewport.clientHeight,crop,d.zoom,d.horizontal,d.vertical,fitMode);
      if(!geometry)return;
      for(const key of ['width','height','left','top'])img.style[key]=`${geometry[key]}px`;
      img.style.transform='none';
      // A contained artwork crop must never reveal HP/attacks in letterboxed margins.
      img.style.clipPath=`inset(${crop.y*geometry.height}px ${(1-crop.x-crop.w)*geometry.width}px ${(1-crop.y-crop.h)*geometry.height}px ${crop.x*geometry.width}px)`;
    }
    const FULL_CROP={x:0,y:0,w:1,h:1};
    function referenceCrop(){return read('crop-mode')==='framed'?{x:.105,y:.215,w:.79,h:.345}:{x:.09,y:.125,w:.82,h:.56};}
    function renderArtwork(){
      if(state.source==='reference'){
        const crop=referenceCrop();
        const fit=read('reference-fit')==='cover'?'cover':'contain';
        // Cover layer fills the otherwise empty space using the *same cropped illustration*.
        // It is blurred purely for preview; do not present it as reconstructed production art.
        applyCoverLayout(by('reference-cover'),by('reference-cover-fill'),crop,'cover');
        applyCoverLayout(by('reference-art'),by('reference-art-fill'),crop,'cover');
        for(const [box,img] of [[by('reference-cover'),by('reference-cover-img')],[by('reference-art'),by('reference-art-img')]]){
          applyCoverLayout(box,img,crop,fit);
          // Feather the sharp picture into its illustration-derived extension.
          // The actual card outside the crop stays clipped, including its frame and stats.
          const x1=100*crop.x,x2=100*(crop.x+crop.w),y1=100*crop.y,y2=100*(crop.y+crop.h);
          const h=100*crop.w*.12,v=100*crop.h*.13;
          img.style.maskImage=`linear-gradient(to bottom,transparent ${y1}%,black ${y1+v}%,black ${y2-v}%,transparent ${y2}%),linear-gradient(to right,transparent ${x1}%,black ${x1+h}%,black ${x2-h}%,transparent ${x2}%)`;
          img.style.maskComposite='intersect';
        }
      }else{
        applyCoverLayout(by('artwork')?.parentElement,art,FULL_CROP);
        applyCoverLayout(by('art-only')?.parentElement,artOnly,FULL_CROP);
      }
    }
    function showCardCrop(){renderArtwork();}
    function updateDownloadAvailability(){
      const referenceOnly=!!state.inspiration || state.source==='reference';
      const blocked=referenceOnly||state.view==='perspective';
      const button=by('download');
      button.disabled=blocked;
      button.title=referenceOnly?'Les cartes de référence ne peuvent pas être téléchargées.':state.view==='perspective'?'Choisissez Avant, Arrière ou Illustration pour obtenir un export fidèle.':'';
      button.setAttribute('aria-disabled',String(blocked));
      by('export-help').textContent=referenceOnly?'Référence visuelle uniquement : export désactivé.':state.view==='perspective'?'Pour exporter, choisissez la vue Avant, Arrière ou Illustration.':'Aperçu conceptuel PNG, non prêt à imprimer.';
    }
    async function previewInspiration(item){
      const token=++state.referenceToken;
      let base=validCardImage(item.image);
      // Older selections only saved the card ID. Recover its CDN path from the API record.
      if(!base&&item.source){
        try{const r=await fetch(item.source,{mode:'cors',credentials:'omit',signal:AbortSignal.timeout(10000)});
          if(r.ok){const json=await r.json();base=validCardImage(json?.image);}
        }catch{}
      }
      if(token!==state.referenceToken||!state.inspiration)return;
      if(!base){updateDownloadAvailability();status('Image non disponible pour cette carte. Référence conservée, aperçu fictif affiché, export désactivé.');return;}
      const candidates=[`${base}/high.webp`,`${base}/low.webp`,`${base}/high.png`,`${base}/low.png`];
      for(const url of candidates){
        const loaded=await new Promise(resolve=>{
          const im=new Image();const timeout=setTimeout(()=>{im.onload=im.onerror=null;resolve(false)},9000);
          im.onload=()=>{clearTimeout(timeout);resolve(true)};
          im.onerror=()=>{clearTimeout(timeout);resolve(false)};
          im.src=url;
        });
        if(token!==state.referenceToken||!state.inspiration)return;
        if(loaded){
          for(const node of [by('reference-cover-img'),by('reference-art-img'),by('reference-cover-fill'),by('reference-art-fill')]){
            node.onload=renderArtwork;
            node.src=url;
          }
          state.source='reference';state.image=url;
          showReference(true);showCardCrop();update();
          status('Illustration de la carte recadrée sur le classeur. Aperçu indicatif uniquement, sans autorisation de reproduction.');
          return;
        }
      }
      status('L’image de la carte ne charge pas. La référence est conservée pour le devis.');
    }
    function clearUploaded(){
      if(state.uploaded)URL.revokeObjectURL(state.uploaded);
      state.uploaded=null;state.uploadName='';state.uploadMeta=null;
      if(by('upload'))by('upload').value='';
      renderUploadHandoff();
    }
    function removeGallery(clear=true){state.gallery=null;root.classList.remove('bas--gallery-art');by('selected-art').hidden=true;if(clear)try{sessionStorage.removeItem(GALLERY_KEY)}catch{}switchArt(sampleArt,'example');status('Décrivez votre projet sur mesure ou sélectionnez une création de la galerie.')}
    function chooseInspiration(ref){
      if(!ref || typeof ref.id!=='string' || !/^[\w.-]{2,80}$/.test(ref.id) || typeof ref.name!=='string')return;
      const item={id:ref.id.slice(0,80),name:ref.name.slice(0,120),set:String(ref.set||'').slice(0,100),number:String(ref.number||'').slice(0,25),category:String(ref.category||'').slice(0,80),source:typeof ref.source==='string'&&/^https:\/\/api\.tcgdex\.net\/v2\/(en|fr)\/cards\/[\w.%-]+$/.test(ref.source)?ref.source:'',image:validCardImage(ref.image)};
      if(state.gallery)removeGallery();
      if(state.uploaded)clearUploaded();
      state.inspiration=item;by('inspiration').hidden=false;
      by('inspiration-title').textContent=item.name;
      by('inspiration-info').textContent=`${item.set} · ${item.number} · ${item.category}`;
      by('subject').value=item.name;
      const cat=item.category.toLowerCase();
      by('complexity').value=cat.includes('special illustration')?'complex':cat.includes('illustration')?'detailed':cat.includes('galarian')?'detailed':'moderate';
      by('reference-fit').value='contain';
      by('crop-mode').value=(cat.includes('illustration')||cat.includes('trainer gallery')||cat.includes('galarian gallery'))?'full':'framed';
      status('Carte enregistrée : préparation de la maquette avec l’illustration recadrée…');update();updateDownloadAvailability();
      previewInspiration(item);
    }
    function chooseGallery(item){
      if(!item||typeof item.id!=='string'||typeof item.title!=='string'||!allowedImage(item.image))return;
      const estimated=Math.max(.5,Math.min(40,Number(item.estimatedHours)||2));
      removeInspiration();
      if(state.uploaded)clearUploaded();
      state.gallery={id:item.id.slice(0,128),title:item.title.slice(0,150),category:String(item.category||'').slice(0,80),image:new URL(item.image,location.href).href,estimatedHours:estimated};
      root.classList.add('bas--gallery-art');by('selected-art').hidden=false;
      by('selected-thumb').src=state.gallery.image;by('selected-thumb').alt=state.gallery.title;
      by('selected-title').textContent=state.gallery.title;
      by('selected-meta').textContent=`${state.gallery.category} · ≈ ${estimated} h (à confirmer)`;
      switchArt(state.gallery.image,'gallery');status('Création approuvée sélectionnée. Ajustez le format, la couleur et le placement.');
    }
    function switchView(view){
      if(!['front','back','perspective','art'].includes(view))return;
      state.view=view;
      by('binder').hidden=view==='art';by('art-alone').hidden=view!=='art';
      by('binder').dataset.previewSide=view==='back'?'back':view==='perspective'?'perspective':'front';
      requestAnimationFrame(renderArtwork);
      root.querySelectorAll('[data-bas-view]').forEach(button=>{const selected=button.dataset.basView===view;button.classList.toggle('is-active',selected);button.setAttribute('aria-pressed',String(selected))});
      by('scene-label').textContent=view==='back'?'DOS DU CLASSEUR · MAQUETTE GÉNÉRIQUE':view==='art'?'ILLUSTRATION SÉPARÉE · CONCEPT SEULEMENT':view==='perspective'?'VUE EN ANGLE · MAQUETTE GÉNÉRIQUE':'AVANT DU CLASSEUR · MAQUETTE GÉNÉRIQUE';
      updateDownloadAvailability();
    }
    // Export uses the exact same geometry as the DOM cover fit (no stretching).
    function drawCover(ctx,img,x,y,w,h,zoom,pX,pY){
      ctx.save();ctx.beginPath();ctx.roundRect(x,y,w,h,9);ctx.clip();
      const g=coverGeometry(img.naturalWidth,img.naturalHeight,w,h,FULL_CROP,zoom*100,pX,pY);
      if(!g)throw Error('Image non chargée');
      ctx.drawImage(img,x+g.left,y+g.top,g.width,g.height);
      const shine=ctx.createLinearGradient(x,y,x+w,y+h);shine.addColorStop(0,'#ffffff22');shine.addColorStop(.45,'#ffffff00');shine.addColorStop(1,'#00000035');ctx.fillStyle=shine;ctx.fillRect(x,y,w,h);ctx.restore();
    }
    async function download(){
      if(state.inspiration || state.source==='reference'){status('Téléchargement indisponible pour les cartes Pokémon de référence.');return}
      if(state.view==='perspective'){status('Choisissez la vue Avant, Arrière ou Illustration pour exporter une maquette fidèle.');return}
      if(state.view!=='back'&&(!art.complete||!art.naturalWidth)){status('L’image est encore en chargement. Réessayez dans un instant.');return}
      try{
        const cv=document.createElement('canvas');cv.width=1050;cv.height=1400;const ctx=cv.getContext('2d');
        const bg=ctx.createLinearGradient(0,0,1050,1400);bg.addColorStop(0,'#f0faff');bg.addColorStop(1,'#fce5f3');ctx.fillStyle=bg;ctx.fillRect(0,0,1050,1400);
        ctx.fillStyle='#486484';ctx.font='bold 29px Arial,sans-serif';ctx.fillText('PRIMACCINO · APERÇU CONCEPTUEL',70,94);
        const d=specs();
        if(state.view==='art'){
          // The standalone preview uses a 3:4 art canvas; its pan and zoom match the screen.
          drawCover(ctx,art,207,207,636,848,d.zoom/100,d.horizontal,d.vertical);
          ctx.font='20px Arial,sans-serif';ctx.fillStyle='#58718a';ctx.fillText('Illustration distincte de la maquette de classeur',70,1310);
        }else{
          const back=state.view==='back';
          ctx.save();ctx.translate(525,700);ctx.rotate((back?4:-5)*Math.PI/180);ctx.shadowColor='#5a7e9380';ctx.shadowBlur=55;ctx.shadowOffsetX=25;ctx.shadowOffsetY=25;
          ctx.beginPath();ctx.roundRect(-310,-456,620,912,37);ctx.fillStyle=binderHex();ctx.fill();ctx.shadowColor='transparent';ctx.shadowOffsetX=ctx.shadowOffsetY=ctx.shadowBlur=0;
          // Match the physical preview's cover aspect ratio and leave an even border.
          const cover=by('artwork').parentElement;
          const aspect=cover.clientWidth&&cover.clientHeight?cover.clientWidth/cover.clientHeight:0.69;
          const cw=548,ch=Math.min(850,cw/aspect);
          if(back){ctx.beginPath();ctx.roundRect(-cw/2,-ch/2,cw,ch,14);ctx.fillStyle=binderHex();ctx.fill();ctx.strokeStyle='#ffffff55';ctx.lineWidth=4;ctx.stroke();ctx.fillStyle='#ffffffdd';ctx.textAlign='center';ctx.font='32px Georgia,serif';ctx.fillText('PRIMACCINO',0,0);ctx.font='18px Arial,sans-serif';ctx.fillText('Dos du classeur · maquette',0,48)}
          else drawCover(ctx,art,-cw/2,-ch/2,cw,ch,d.zoom/100,d.horizontal,d.vertical);
          ctx.lineWidth=3;ctx.strokeStyle='#ffffff6a';ctx.setLineDash([6,8]);ctx.beginPath();ctx.roundRect(-298,-444,596,888,25);ctx.stroke();ctx.setLineDash([]);ctx.restore();
          ctx.font='19px Arial,sans-serif';ctx.fillStyle='#58718a';ctx.fillText(`${d.color} · ${d.size}`,70,1310);
        }
        ctx.font='16px Arial,sans-serif';ctx.fillStyle='#58718a';ctx.fillText('Simulation numérique : dimensions et réalisation à confirmer avec l’artiste',70,1350);
        const blob=await new Promise(r=>cv.toBlob(r,'image/png'));if(!blob)throw Error('PNG indisponible');
        const u=URL.createObjectURL(blob),a=document.createElement('a');a.href=u;a.download='primaccino-aperçu-classeur.png';document.body.append(a);a.click();a.remove();setTimeout(()=>URL.revokeObjectURL(u),4000);status('Maquette PNG enregistrée.');
      }catch(e){status('Export impossible. Vérifiez que l’image est chargée et autorisée, puis réessayez.');console.warn('[Primaccino] Export:',e)}
    }
    // Machine-readable quote manifest embedded inside Shopify contact[body].
    // It is untrusted browser data: the artist / future backend MUST revalidate prices,
    // artwork permissions, stock, identity and payment before making any order.
    // PII such as name and email are managed by Shopify's contact fields, not this manifest.
    function quoteManifest(d,price){
      const chosenSize=by('binder-size')?.selectedOptions?.[0];
      const artworkKind=state.gallery?'catalogue_approved':state.inspiration?'pokemon_inspiration':state.uploaded?'customer_file': 'custom_description';
      return {
        schema:'primaccino.quote_request.v1',
        reference:requestId,
        created_at:new Date().toISOString(),
        state:'quote_requested',
        submission:'shopify_contact_form',
        payment_status:'NOT_PAID',
        artist_review_required:true,
        production_method:'printed_base_then_hand_painted',
        binder:{format_code:line(chosenSize?.dataset.format||'3x3',32),format_label:line(d.size,100),physical_color:line(d.color,40)},
        artwork:{kind:artworkKind,production_permission:state.gallery?'catalogue_claim_requires_artist_verification':'not_authorized_for_reproduction',
          catalogue_id:state.gallery?.id||null,catalogue_title:state.gallery?.title||null,catalogue_category:state.gallery?.category||null,
          approved_catalogue_image:state.gallery?.image||null,
          card_id:state.inspiration?.id||null,card_name:state.inspiration?.name||null,
          card_set:state.inspiration?.set||null,card_number:state.inspiration?.number||null,
          card_category:state.inspiration?.category||null,card_reference_source:state.inspiration?.source||null,
          customer_file_name:state.uploaded?state.uploadName:null,
          customer_file_type:state.uploaded?state.uploadMeta?.type||null:null,
          customer_file_size:state.uploaded?state.uploadMeta?.size||null:null,
          customer_file_attached:false,
          customer_file_handoff:state.uploaded?'manual_follow_up_required':'not_applicable'},
        creative_brief:{subject:line(d.subject,100),description:read('details').slice(0,500),personalized_text:read('custom-text').slice(0,40),customer_note:read('customer-note').slice(0,700)},
        preview:{zoom_percent:d.zoom,horizontal_percent:d.horizontal,vertical_percent:d.vertical,
          reference_crop:state.inspiration?read('crop-mode'):null,
          reference_fit:state.inspiration?read('reference-fit'):null,
          preview_not_production_artwork:state.source==='reference'||state.source==='example'},
        estimated_pricing:{currency:'CAD',binder:price.base,format_surcharge:price.sizeFee,
          artist_hours:price.hours,artist_hourly_rate:price.hourly,artist_labor:price.labor,
          reseller_fee:price.reseller,printing:price.printing,materials:price.materials,
          customer_estimate:price.total,taxes_included:false,shipping_included:false,
          verified_by_server:false,final_price_requires_artist_approval:true},
        delivery:{region:line(read('delivery-region'),100),requested_date:line(read('requested-date'),15),requested_date_guaranteed:false},
        artist_process:{review_note:line(root.dataset.reviewNote,180),production_note:line(root.dataset.productionNote,180),approval_before_invoice:true}
      };
    }
    function prepareQuote(event){
      const requestedDate=read('requested-date');
      if(requestedDate){
        // Use the customer's LOCAL calendar day, not toISOString() (which uses UTC).
        const now=new Date();
        const today=[now.getFullYear(),String(now.getMonth()+1).padStart(2,'0'),String(now.getDate()).padStart(2,'0')].join('-');
        if(requestedDate<today){event.preventDefault();goToStep(3);status('Choisis une date souhaitée aujourd’hui ou plus tard. Ce n’est pas une date garantie.');by('requested-date').focus();return;}
      }
      if(state.uploaded&&!by('upload-confirm').checked){event.preventDefault();goToStep(3);status('Cochez la confirmation : votre image doit être envoyée séparément à l’artiste.');by('upload-confirm').focus();return}
      const d=specs();if(!state.gallery&&!d.subject&&!state.inspiration&&!state.uploaded&&!d.details){event.preventDefault();goToStep(1);status('Indiquez un thème, importez un dessin ou décrivez votre idée avant de demander un devis.');by('subject').focus();return}
      const price=pricing();
      const lines=[
        'PRIMACCINO — DEMANDE DE DEVIS (NE PAS CRÉER DE COMMANDE AVANT VALIDATION)',
        `Référence du projet : ${requestId}`,
        'Statut : À examiner par l’artiste — aucun paiement effectué',
        `Type : ${state.gallery?'Œuvre autorisée du catalogue':'Création sur mesure — contacter l’artiste'}`,
        `Sujet : ${line(d.subject,100)||'À définir'}`,
        `Illustration approuvée : ${state.gallery?`${line(state.gallery.title,150)} (${line(state.gallery.id,128)})`:'Aucune'}`,
        `Carte Pokémon de référence (inspiration uniquement) : ${state.inspiration?`${line(state.inspiration.name,120)} · ${line(state.inspiration.set,100)} · N° ${line(state.inspiration.number,25)} · ${line(state.inspiration.category,80)} [${line(state.inspiration.id,80)}]`:'Aucune'}`,
        `Source de référence : ${line(state.inspiration?.source,300)||'Sans objet'}`,
        'Une référence Pokémon n’accorde pas le droit de reproduire son illustration : vérifier l’autorisation avant toute fabrication.',
        `Description libre : ${line(d.details,500)||'Aucune'}`,`Texte optionnel demandé : ${line(read('custom-text'),40)||'Aucun'}`,`Classeur : ${line(d.size,100)} · ${line(d.color,40)}`,
        `Placement : zoom ${d.zoom} % ; horizontal ${d.horizontal} % ; vertical ${d.vertical} %`,
        `Fabrication : ${PROCESS}`,
        `Difficulté estimée : ${complexityName()} · ${precision(productionHours())} h · ${state.gallery?'définie par l’atelier':'niveau provisoire, non validé'}`,
        `Recadrage de référence : ${state.inspiration?read('crop-mode'):'sans objet'} · aperçu non autorisé pour production`,
        `Illustration actuellement prévisualisée : ${state.source==='gallery'?'Fichier autorisé en catalogue — '+state.gallery.image:state.source==='upload'?'Fichier client à transmettre séparément':state.source==='reference'?'Illustration de carte recadrée en aperçu uniquement (non autorisée pour impression)':'Maquette fictive uniquement (aucune image finale créée)'}`,
        `Fichier client à transmettre séparément : ${state.uploaded ? `${state.uploadName} (${state.uploadMeta?.type||'type inconnu'}, ${state.uploadMeta?.size||0} octets, NON JOINT AU FORMULAIRE)` : 'Aucun'}`,
        `ESTIMATION NON CONTRACTUELLE : ${money(price.total)} avant taxes et livraison`,
        `Détail du PRIX CLIENT : classeur ${money(price.base)} + supplément de format ${money(price.sizeFee)} + rémunération artistique ${precision(price.hours)} h × ${money(price.hourly)} = ${money(price.labor)} + service de revente ${money(price.reseller)} + préparation/impression ${money(price.printing)} + finitions/fournitures ${money(price.materials)}`,
        'La rémunération de revente ne représente pas nécessairement le profit net. Coût réel du classeur, frais de paiement, impôts et autres charges à rapprocher séparément en comptabilité.' ,
        `Région approximative de livraison (sans adresse complète) : ${line(read('delivery-region'),100)||'À préciser'}`,
        `Date souhaitée (NON GARANTIE) : ${line(read('requested-date'),15)||'Aucune'}`,
        `Délai de réponse annoncé : ${line(root.dataset.reviewNote,180)||'À confirmer'}`,
        `Délai de fabrication annoncé : ${line(root.dataset.productionNote,180)||'À confirmer'}`,
        `Remarques du client : ${line(read('customer-note'),700)||'Aucune'}`,
        'PROCHAINES ÉTAPES : vérifier les droits et la faisabilité ; confirmer le prix définitif par courriel ; si accepté créer un brouillon de commande Shopify ; envoyer la facture Shopify ; fabriquer après paiement.',
        'La faisabilité, les droits de reproduction, les coûts et le prix final doivent être approuvés par l’artiste avant paiement.'
      ];
      const manifest=quoteManifest(d,price);
      lines.push('--- PRIMACCINO_REQUEST_JSON_V1_BEGIN ---',JSON.stringify(manifest),'--- PRIMACCINO_REQUEST_JSON_V1_END ---');
      by('quote-body').value=lines.join('\n');
      try{sessionStorage.setItem(LAST_SUBMITTED_KEY,requestId);sessionStorage.setItem(LAST_UPLOAD_KEY,state.uploaded?'yes':'no')}catch{}
    }

    // V0.9.7: browser-only design draft. No customer contact information or uploaded file bytes.
    // The original Shopify contact form remains the only quote submission mechanism.
    let wizardStep=0;
    const stepTitles=['Mon classeur','Mon illustration','Mon aperçu','Mon devis'];
    const nextTitles=['Choisir mon illustration →','Voir mon aperçu →','Vérifier mon devis →','Voir le formulaire et le prix ↓'];
    const defaultInputs={subject:'',details:'','custom-text':'',complexity:'moderate',crop:'full','reference-fit':'contain',zoom:'100',horizontal:'50',vertical:'50'};
    function readDraft(){
      let value=null;
      try{value=localStorage.getItem(DRAFT_KEY)||sessionStorage.getItem(DRAFT_KEY)}catch{}
      if(!value)return null;
      let draft=null;
      try{draft=JSON.parse(value)}catch{}
      if(!draft || typeof draft!=='object' || draft.version!==1 ||
         !Number.isFinite(draft.savedAt) || Date.now()-draft.savedAt>DRAFT_TTL ||
         draft.savedAt>Date.now()+60_000){clearDraft();return null}
      return draft;
    }
    function clearDraft(){
      try{localStorage.removeItem(DRAFT_KEY)}catch{}
      try{sessionStorage.removeItem(DRAFT_KEY)}catch{}
    }
    function noteDraft(message){const panel=by('draft-message');if(panel)panel.textContent=message;}
    function saveWizard({announce=false}={}){
      const chosen=colourChoice();
      const draft={version:1,savedAt:Date.now(),step:wizardStep,color:chosen?.value||'',
        size:read('binder-size'),subject:read('subject'),details:read('details'),customText:read('custom-text'),
        complexity:read('complexity'),crop:read('crop-mode'),referenceFit:read('reference-fit'),zoom:val('zoom',100),
        horizontal:val('horizontal'),vertical:val('vertical'),view:state.view,
        gallery:state.gallery?{...state.gallery}:null,
        inspiration:state.inspiration?{...state.inspiration}:null,
        // Local file contents and object URLs are deliberately NOT stored.
        hadUpload:!!state.uploaded};
      try{localStorage.setItem(DRAFT_KEY,JSON.stringify(draft));if(announce)noteDraft('♡ Projet enregistré ici pour 7 jours. Images importées non sauvegardées.');}
      catch{try{sessionStorage.setItem(DRAFT_KEY,JSON.stringify(draft));if(announce)noteDraft('♡ Projet enregistré pour cette session seulement.')}catch{noteDraft('Sauvegarde indisponible dans ce navigateur.')}}
    }
    function restoreWizard(draft){
      if(!draft)return 0;
      const bind={size:'binder-size',subject:'subject',details:'details',customText:'custom-text',complexity:'complexity',crop:'crop-mode',referenceFit:'reference-fit',zoom:'zoom',horizontal:'horizontal',vertical:'vertical'};
      for(const [key,targetName] of Object.entries(bind)){
        const target=by(targetName);if(!target||draft[key]==null)continue;
        const v=String(draft[key]).slice(0,500);
        if(target.tagName==='SELECT' && !Array.from(target.options).some(o=>o.value===v&&!o.disabled))continue;
        if(target.type==='range'){
          const number=Number(v);if(!Number.isFinite(number))continue;
          target.value=String(Math.max(Number(target.min),Math.min(Number(target.max),number)));
        }else target.value=v;
      }
      for(const radio of root.querySelectorAll('[name^="bas-binder-color-"]')){
        if(radio.value===draft.color){radio.checked=true;break;}
      }
      const currentSize=by('binder-size');if(currentSize?.selectedOptions?.[0]?.disabled){const available=Array.from(currentSize.options).find(o=>!o.disabled);if(available)currentSize.value=available.value;}
      if(['front','back','perspective','art'].includes(draft.view))switchView(draft.view);
      if(draft.gallery && typeof draft.gallery==='object')chooseGallery(draft.gallery);
      else if(draft.inspiration && typeof draft.inspiration==='object')chooseInspiration(draft.inspiration);
      // Never claim a previously uploaded file is still available after reloading.
      if(draft.hadUpload)noteDraft('Ton projet a été repris. Pour revoir ton image personnelle, importe-la à nouveau.');
      else noteDraft('♡ Projet retrouvé sur cet appareil. Modifie-le quand tu veux.');
      return Number.isInteger(draft.step)?Math.max(0,Math.min(3,draft.step)):0;
    }
    function resetProject(){
      if(!window.confirm('Recommencer ton classeur ? Les choix enregistrés seront effacés.'))return;
      clearDraft();
      try{sessionStorage.removeItem(GALLERY_KEY);sessionStorage.removeItem(REFERENCE_KEY)}catch{}
      if(state.uploaded)clearUploaded();
      if(state.gallery)removeGallery(false);
      if(state.inspiration)removeInspiration(false);
      for(const [key,value] of Object.entries(defaultInputs)){const el=by(key==='crop'?'crop-mode':key);if(el)el.value=value;}
      const first=root.querySelector('[name^="bas-binder-color-"]');if(first)first.checked=true;
      const size=by('binder-size');if(size){const current=size.selectedOptions?.[0];if(!current||current.disabled){const available=Array.from(size.options).find(o=>!o.disabled);if(available)size.value=available.value;}}
      state.gallery=null;state.inspiration=null;
      root.classList.remove('bas--gallery-art');by('selected-art').hidden=true;by('inspiration').hidden=true;
      switchArt(sampleArt,'example');switchView('front');
      requestId=newRequestId();if(by('request-id'))by('request-id').textContent=requestId;
      try{sessionStorage.setItem(REQUEST_KEY,requestId)}catch{}
      by('upload-confirm').checked=false;renderUploadHandoff();
      // Starting a new project must not retain the previous person's contact/delivery fields.
      for(const name of ['delivery-region','requested-date','customer-note']){const node=by(name);if(node)node.value='';}
      const form=root.querySelector('form[action*="/contact"]');
      if(form){for(const name of ['contact[name]','contact[email]']){const node=form.elements.namedItem(name);if(node&&'value' in node)node.value='';}}
      if(by('acknowledgment'))by('acknowledgment').checked=false;
      goToStep(0,{save:false});update();refreshRecap();
      noteDraft('Nouveau projet prêt. Aucun ancien choix conservé.');
      status('Commence par choisir la couleur et le format de ton classeur.');
    }
    by('reset-project').addEventListener('click',resetProject);
    by('edit-artwork')?.addEventListener('click',()=>goToStep(1));
    function refreshRecap(){
      by('recap-binder').textContent=`${binderColor()} · ${read('binder-size')||'Format à confirmer'}`;
      by('recap-art').textContent=state.gallery?.title|| (state.inspiration?`${state.inspiration.name} · référence seulement`: read('subject')|| (state.uploaded?'Fichier fourni par le client':read('details')?'Projet sur mesure':'Aucun design choisi'));
      const optionalText=read('custom-text');by('recap-text-row').hidden=!optionalText;by('recap-text').textContent=optionalText.slice(0,40);
      by('recap-complexity').textContent=`${complexityName()} · ${precision(productionHours())} h estimées`;
      by('recap-placement').textContent=`Zoom ${read('zoom')} % · H ${read('horizontal')} % · V ${read('vertical')} %`;
    }
    function goToStep(index,{scroll=true,save=true}={}){
      wizardStep=Math.max(0,Math.min(3,Number(index)||0));
      root.dataset.wizardStep=String(wizardStep);
      root.querySelectorAll('[data-bas-wizard-pane]').forEach(p=>{p.hidden=Number(p.dataset.basWizardPane)!==wizardStep});
      root.querySelectorAll('[data-bas-step-jump]').forEach(b=>{
        const step=Number(b.dataset.basStepJump);
        if(step===wizardStep)b.setAttribute('aria-current','step');else b.removeAttribute('aria-current');
        b.classList.toggle('is-completed',step<wizardStep);
      });
      by('wizard-progress').textContent=`Étape ${wizardStep+1} sur 4`;
      by('wizard-track').setAttribute('aria-valuenow',String(wizardStep+1));
      by('wizard-progress-fill').style.width=`${(wizardStep+1)*25}%`;
      by('wizard-back').disabled=wizardStep===0;
      by('wizard-next').hidden=false;
      by('wizard-next').textContent=nextTitles[wizardStep];
      const heading=root.querySelector('.bas__editor-heading h2');if(heading)heading.textContent=stepTitles[wizardStep]+' ♡';
      refreshRecap();
      requestAnimationFrame(renderArtwork);
      if(save)saveWizard();
      if(scroll){root.querySelector('[data-bas="wizard-nav"]').scrollIntoView({behavior:'smooth',block:'start'});}
    }
    function canAdvanceFromArtwork(){
      if(state.gallery || state.inspiration || state.uploaded || read('subject') || read('details'))return true;
      status('Choisissez une inspiration, une illustration ou décrivez votre idée pour poursuivre.');
      const target=by('subject');target?.focus({preventScroll:true});return false;
    }
    root.querySelectorAll('[data-bas-step-jump]').forEach(button=>button.addEventListener('click',()=>{
      const target=Number(button.dataset.basStepJump);
      if(target>1&&wizardStep<=1&&!canAdvanceFromArtwork()) {goToStep(1);return}
      goToStep(target);
    }));
    by('wizard-back').addEventListener('click',()=>goToStep(wizardStep-1));
    by('wizard-next').addEventListener('click',()=>{
      if(wizardStep===1&&!canAdvanceFromArtwork())return;
      if(wizardStep===3){root.querySelector('.bas__quote-wrap')?.scrollIntoView({behavior:'smooth',block:'start'});return;}
      goToStep(wizardStep+1);
    });
    // Save only non-sensitive design choices when a user changes a setting.
    root.querySelectorAll('input,select,textarea').forEach(el=>{
      if((el.hasAttribute('data-bas')&&!['customer-note','delivery-region','requested-date','acknowledgment','quote-body'].includes(el.dataset.bas))||el.matches('[name^="bas-binder-color-"]')){
        el.addEventListener('change',()=>{refreshRecap();saveWizard()});
        if(el.matches('[data-bas="subject"],[data-bas="details"],[data-bas="custom-text"],input[type="range"]'))el.addEventListener('input',()=>{refreshRecap();saveWizard()});
      }
    });
    root.querySelectorAll('input,select,textarea').forEach(el=>{
      if(el.hasAttribute('data-bas')||el.matches('[name^="bas-binder-color-"]')){el.addEventListener('input',update);el.addEventListener('change',update)}
    });
    root.querySelectorAll('[data-bas-view]').forEach(button=>button.addEventListener('click',()=>switchView(button.dataset.basView)));
    by('reset-placement').addEventListener('click',()=>{by('zoom').value=100;by('horizontal').value=50;by('vertical').value=50;update();saveWizard();status('Placement recentré, sans déformation de l’image.')});
    by('download').addEventListener('click',download);
    by('crop-mode').addEventListener('change',()=>{if(state.source==='reference')showCardCrop();update()});
    by('reference-fit').addEventListener('change',()=>{renderArtwork();saveWizard();update()});
    by('upload').addEventListener('change',event=>{
      const file=event.target.files?.[0];if(!file)return;
      if(!['image/jpeg','image/png','image/webp'].includes(file.type)||file.size>12_000_000){
        event.target.value='';
        status('Image PNG, JPG ou WebP de moins de 12 Mo uniquement. Le fichier invalide a été ignoré.');
        return;
      }
      if(state.gallery)removeGallery();
      if(state.inspiration)removeInspiration();
      if(state.uploaded)URL.revokeObjectURL(state.uploaded);
      state.uploadName=line(file.name||'illustration',120);
      state.uploadMeta={type:file.type,size:file.size};
      state.uploaded=URL.createObjectURL(file);
      switchArt(state.uploaded,'upload');
      by('upload-confirm').checked=false;
      renderUploadHandoff();
      status('Aperçu prêt. Important : vous devrez transmettre le fichier original à l’artiste séparément, avec votre référence de projet.');saveWizard();
    });
    const quoteForm=root.querySelector('form[action*="/contact"]');
    quoteForm?.addEventListener('submit',event=>{
      prepareQuote(event);
      if(event.defaultPrevented)return;
      const button=by('submit-request');
      if(button){button.disabled=true;button.setAttribute('aria-busy','true');button.textContent='Envoi de la demande…';}
      // Do not create draft orders on the storefront; merchant approval happens in Shopify Admin.
    });
    const successPanel=by('quote-success');
    if(successPanel){
      let confirmed='';try{confirmed=sessionStorage.getItem(LAST_SUBMITTED_KEY)||''}catch{}
      if(/^PR-\d{6}-[0-9A-F]{10}$/.test(confirmed)){
        by('confirmed-request-id').textContent=confirmed;
        let needsImage=false;try{needsImage=sessionStorage.getItem(LAST_UPLOAD_KEY)==='yes'}catch{}
        by('upload-success-note').hidden=!needsImage;
        const link=by('upload-success-mail');link.hidden=!needsImage||!validArtworkEmail;
        if(needsImage&&validArtworkEmail)link.href=imageMailHref(confirmed);
        const copy=by('copy-request-id');copy.hidden=false;
        copy.addEventListener('click',async()=>{
          try{await navigator.clipboard.writeText(confirmed);copy.textContent='Référence copiée ✓'}catch{copy.textContent=confirmed}
        });
      }
      // Create a fresh reference for any later request; previous reference remains visible in the confirmation.
      requestId=newRequestId();if(by('request-id'))by('request-id').textContent=requestId;
      try{sessionStorage.setItem(REQUEST_KEY,requestId);sessionStorage.removeItem(LAST_SUBMITTED_KEY);sessionStorage.removeItem(LAST_UPLOAD_KEY);sessionStorage.removeItem(DRAFT_KEY)}catch{}
      try{localStorage.removeItem(DRAFT_KEY)}catch{}
    }
    by('remove-art').addEventListener('click',()=>{removeGallery();saveWizard();});
    by('remove-inspiration').addEventListener('click',()=>{removeInspiration();saveWizard();});
    window.addEventListener('primaccino:inspiration-selected',e=>{chooseInspiration(e.detail);goToStep(1,{scroll:false});saveWizard();});
    window.addEventListener('primaccino:artwork-selected',e=>{chooseGallery(e.detail);goToStep(1,{scroll:false});saveWizard();});
    const draft=readDraft();
    let draftStep=restoreWizard(draft);
    // Backward compatibility: selection made earlier in this session takes precedence
    // over the last saved project when navigating back from a gallery.
    try{const r=sessionStorage.getItem(REFERENCE_KEY);if(r){chooseInspiration(JSON.parse(r));draftStep=1;}}catch{}
    try{const r=sessionStorage.getItem(GALLERY_KEY);if(r){chooseGallery(JSON.parse(r));draftStep=1;}}catch{}
    // Image and viewport resize hooks keep approved art and references aligned on mobile, desktop and view switching.
    for(const img of [art,artOnly]){img.addEventListener('load',renderArtwork);img.addEventListener('error',()=>{if(state.source!=='reference')status('L’image ne peut pas être affichée. Essayez une autre illustration ou contactez l’atelier.')});}
    for(const img of [by('reference-cover-img'),by('reference-art-img'),by('reference-cover-fill'),by('reference-art-fill')])img.addEventListener('load',renderArtwork);
    if(typeof ResizeObserver!=='undefined'){const resizeObserver=new ResizeObserver(()=>requestAnimationFrame(renderArtwork));
      for(const selector of ['artwork','art-only','reference-cover','reference-art']){const el=by(selector);resizeObserver.observe(selector==='artwork'||selector==='art-only'?el.parentElement:el);}
    }else window.addEventListener('resize',renderArtwork);
    if(!draft)switchView('front');
    update();updateDownloadAvailability();
    renderUploadHandoff();
    goToStep((state.gallery||state.inspiration)&&draftStep===0?1:draftStep,{scroll:false});
    if(root.querySelector('.bas__success,.bas__error'))goToStep(3,{scroll:false});
    status('Personnalisez votre classeur étape par étape, puis demandez un devis à l’artiste.');
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>boot());else boot();
  document.addEventListener('shopify:section:load',e=>boot(e.target));
})();
